import * as XLSX from "xlsx";

export interface CellStyle {
  backgroundColor?: string;
  color?: string;
  fontWeight?: string;
  textAlign?: "left" | "right" | "center" | "justify" | "start" | "end";
  borderTop?: string;
  borderRight?: string;
  borderBottom?: string;
  borderLeft?: string;
  fontSize?: string;
}

export interface CellData {
  value: any;
  formula?: string;
  type?: string;
  style?: CellStyle;
  format?: string;
  /**
   * Raw numeric value (`cell.v`) for numeric cells only. `value` holds the
   * FORMATTED display string (`cell.w`, e.g. "1 234,50 €"), which cannot be
   * summed reliably — this is the lossless source of truth for selection math
   * (sum / average). Absent for text/date/empty cells.
   */
  numericValue?: number;
}

export interface ParsedSheet {
  rows: CellData[][];
  columnWidths: number[];
  rowHeights: number[];
  /**
   * Absolute 1-based physical sheet row (A1 = 1) for each entry in `rows`,
   * i.e. `rowNumbers[i] = visibleRows[i] + 1`. Hidden rows leave gaps in this
   * numbering — it intentionally does NOT equal `i + 1` once any row is
   * hidden. This is the single source of truth the AI-emitted row number and
   * the ExcelViewer highlight/row-header must agree on (STK-1299).
   */
  rowNumbers: number[];
}

const DEFAULT_COL_WIDTH = 100;
const DEFAULT_ROW_HEIGHT = 25;
const BORDER_STYLE = "1px solid #d1d5db";

function parseCellStyle(rawStyle: any): CellStyle {
  const style: CellStyle = {};

  if (rawStyle.fgColor?.rgb) {
    // Skip theme/indexed colors (no rgb) — falling back to FFFFFF would paint
    // the cell solid white instead of leaving the default transparent bg.
    style.backgroundColor = `#${rawStyle.fgColor.rgb}`;
  }

  if (rawStyle.font) {
    if (rawStyle.font.color?.rgb) {
      style.color = `#${rawStyle.font.color.rgb}`;
    }
    if (rawStyle.font.bold) {
      style.fontWeight = "bold";
    }
    if (rawStyle.font.sz) {
      style.fontSize = `${rawStyle.font.sz}pt`;
    }
  }

  if (rawStyle.alignment?.horizontal) {
    style.textAlign = rawStyle.alignment.horizontal as CellStyle["textAlign"];
  }

  if (rawStyle.border) {
    if (rawStyle.border.top) style.borderTop = BORDER_STYLE;
    if (rawStyle.border.right) style.borderRight = BORDER_STYLE;
    if (rawStyle.border.bottom) style.borderBottom = BORDER_STYLE;
    if (rawStyle.border.left) style.borderLeft = BORDER_STYLE;
  }

  return style;
}

export function parseSheet(
  workbook: XLSX.WorkBook,
  sheetName: string,
): ParsedSheet | null {
  const worksheet = workbook.Sheets[sheetName];
  if (!worksheet) return null;

  const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1");
  const rows: CellData[][] = [];
  const columnWidths: number[] = [];
  const rowHeights: number[] = [];

  const wsCols = worksheet["!cols"] || [];
  const wsRows = worksheet["!rows"] || [];

  // Columns/rows the author hid are dropped entirely (matching Gmail / Numbers).
  // We collect the visible indices, then build densely-packed output arrays so
  // the viewer renders contiguously without needing to know about the gaps.
  const visibleCols: number[] = [];
  for (let C = range.s.c; C <= range.e.c; ++C) {
    if (wsCols[C]?.hidden === true) continue;
    visibleCols.push(C);
  }
  const visibleRows: number[] = [];
  for (let R = range.s.r; R <= range.e.r; ++R) {
    if (wsRows[R]?.hidden === true) continue;
    visibleRows.push(R);
  }

  for (const C of visibleCols) {
    const colInfo = wsCols[C];
    columnWidths.push(
      colInfo?.wpx || colInfo?.width
        ? colInfo.wpx || (colInfo.width ? colInfo.width * 7 : DEFAULT_COL_WIDTH)
        : DEFAULT_COL_WIDTH,
    );
  }

  for (const R of visibleRows) {
    const rowInfo = wsRows[R];
    rowHeights.push(
      rowInfo?.hpx || rowInfo?.hpt
        ? rowInfo.hpx || (rowInfo.hpt ? rowInfo.hpt * 1.33 : DEFAULT_ROW_HEIGHT)
        : DEFAULT_ROW_HEIGHT,
    );

    const row: CellData[] = [];
    for (const C of visibleCols) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = worksheet[cellAddress];

      if (!cell) {
        row.push({ value: "" });
        continue;
      }

      const cellData: CellData = {
        value: cell.w ?? cell.v ?? "",
        formula: cell.f,
        type: cell.t,
        format: cell.z,
      };

      if (cell.s) {
        cellData.style = parseCellStyle(cell.s);
      }

      if (cell.t === "n" && typeof cell.v === "number") {
        cellData.numericValue = cell.v;
      }

      row.push(cellData);
    }
    rows.push(row);
  }

  const rowNumbers = visibleRows.map((r) => r + 1);

  return { rows, columnWidths, rowHeights, rowNumbers };
}

export function formatCellValue(cellData: CellData | undefined): string {
  if (!cellData || cellData.value === undefined || cellData.value === null) {
    return "";
  }

  if (typeof cellData.value === "string") {
    return cellData.value;
  }

  if (typeof cellData.value === "number") {
    if (cellData.format) {
      if (cellData.format.includes("%")) {
        return `${(cellData.value * 100).toFixed(2)}%`;
      }
      if (cellData.format.includes("$")) {
        return `$${cellData.value.toFixed(2)}`;
      }
      if (cellData.format.includes("€")) {
        return `€${cellData.value.toFixed(2)}`;
      }
    }
    return cellData.value.toString();
  }

  if (cellData.type === "d" || cellData.value instanceof Date) {
    const date =
      cellData.value instanceof Date
        ? cellData.value
        : new Date(cellData.value);
    return date.toLocaleDateString();
  }

  return String(cellData.value);
}

export function getColumnHeader(index: number): string {
  let header = "";
  let i = index;
  while (i >= 0) {
    header = String.fromCharCode((i % 26) + 65) + header;
    i = Math.floor(i / 26) - 1;
  }
  return header;
}
